/**
 * Renders a structured table from AI response. Supports sorting, export to Excel,
 * copy cell/row, pagination. Styled for ERP theme (light/dark via MUI theme).
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  Pagination,
  IconButton,
  Tooltip,
  Box,
  Menu,
  MenuItem,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TableChartIcon from "@mui/icons-material/TableChart";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import styles from "../AIChat/AIComponents/EnquiryList.module.scss";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function formatCellValue(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}/)) {
    try {
      return new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch (_) {}
  }
  return String(value);
}

function getHeadersAndData(columns, rows, columnKeys, visibleIndices) {
  const cols = visibleIndices == null
    ? columns
    : visibleIndices.map((i) => columns[i]).filter(Boolean);
  const keys = visibleIndices == null
    ? columnKeys
    : visibleIndices.map((i) => columnKeys[i]).filter(Boolean);
  const headers = cols.map((col) =>
    typeof col === "string" ? col : (col.label || col.key || String(col))
  );
  const data = rows.map((row) =>
    keys.map((key, i) => {
      const v = row[key] ?? row[cols[i]] ?? "";
      return v == null ? "" : String(v);
    })
  );
  return { headers, data, keys };
}

function exportToExcel(columns, rows, columnKeys, exportFilename, visibleIndices) {
  try {
    const XLSX = require("xlsx");
    const { headers, data } = getHeadersAndData(columns, rows, columnKeys, visibleIndices);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const dateStr = new Date().toISOString().slice(0, 10);
    const name = (exportFilename || "export").replace(/\.xlsx$/i, "") + "_" + dateStr + ".xlsx";
    XLSX.writeFile(wb, name);
  } catch (e) {
    console.error("Export failed", e);
  }
}

function exportToCsv(columns, rows, columnKeys, exportFilename, visibleIndices) {
  try {
    const { headers, data } = getHeadersAndData(columns, rows, columnKeys, visibleIndices);
    const escape = (v) => {
      const s = String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.map(escape).join(","), ...data.map((row) => row.map(escape).join(","))];
    const csv = lines.join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const { saveAs } = require("file-saver");
    const dateStr = new Date().toISOString().slice(0, 10);
    const name = (exportFilename || "export").replace(/\.(xlsx|csv)$/i, "") + "_" + dateStr + ".csv";
    saveAs(blob, name);
  } catch (e) {
    console.error("Export CSV failed", e);
  }
}

function copyTableToClipboard(columns, rows, columnKeys, visibleIndices) {
  const { headers, data } = getHeadersAndData(columns, rows, columnKeys, visibleIndices);
  const lines = [headers.join("\t"), ...data.map((row) => row.join("\t"))];
  navigator.clipboard?.writeText(lines.join("\n"));
}

function AIResponseTable({
  title,
  columns = [],
  rows = [],
  actions = [],
  rowClickNavigate,
  getRowId,
  pageSize: initialPageSize = DEFAULT_PAGE_SIZE,
  exportFilename,
  suggestions,
  onSuggestionClick,
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sortKey, setSortKey] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [visibleColIndices, setVisibleColIndices] = useState(null);
  const [showMoreAnchor, setShowMoreAnchor] = useState(null);
  const [hideColAnchor, setHideColAnchor] = useState(null);
  const [quickSearch, setQuickSearch] = useState("");
  const navigate = useNavigate();

  const effectiveColumns = useMemo(() => {
    if (visibleColIndices == null) return columns;
    return visibleColIndices.map((i) => columns[i]).filter(Boolean);
  }, [columns, visibleColIndices]);
  const effectiveColumnKeys = useMemo(() => {
    return effectiveColumns.map((col) => {
      if (typeof col === "object" && col.key != null) return col.key;
      const label = typeof col === "string" ? col : "";
      return label.toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
    });
  }, [effectiveColumns]);

  const columnKeys = useMemo(() => {
    return columns.map((col) => {
      if (typeof col === "object" && col.key != null) return col.key;
      const label = typeof col === "string" ? col : "";
      return label.toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
    });
  }, [columns]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const idx = columnKeys.indexOf(sortKey);
    if (idx < 0) return rows;
    return [...rows].sort((a, b) => {
      const va = a[sortKey] ?? a[columns[idx]];
      const vb = b[sortKey] ?? b[columns[idx]];
      const cmp = String(va ?? "").localeCompare(String(vb ?? ""), undefined, { numeric: true });
      return sortAsc ? cmp : -cmp;
    });
  }, [rows, sortKey, sortAsc, columnKeys, columns]);

  const filteredRows = useMemo(() => {
    if (!quickSearch.trim()) return sortedRows;
    const q = quickSearch.trim().toLowerCase();
    return sortedRows.filter((row) =>
      columnKeys.some((k) => {
        const v = row[k];
        return v != null && String(v).toLowerCase().includes(q);
      })
    );
  }, [sortedRows, quickSearch, columnKeys]);

  const effectivePageSize = pageSize === "all" ? Math.max(filteredRows.length, 1) : pageSize;
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / effectivePageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * effectivePageSize;
  const paginatedRows = useMemo(
    () => filteredRows.slice(start, start + effectivePageSize),
    [filteredRows, start, effectivePageSize]
  );

  const handleExportExcel = () => {
    exportToExcel(columns, filteredRows, columnKeys, exportFilename, visibleColIndices ?? undefined);
  };
  const handleExportCsv = () => {
    exportToCsv(columns, filteredRows, columnKeys, exportFilename, visibleColIndices ?? undefined);
  };
  const handleCopyTable = () => {
    copyTableToClipboard(columns, filteredRows, columnKeys, visibleColIndices ?? undefined);
  };

  const handleCopyCell = (e, value) => {
    e.stopPropagation();
    const text = value != null && value !== "" ? String(value) : "";
    if (text) navigator.clipboard?.writeText(text);
  };

  const copyRowKeys = visibleColIndices != null
    ? visibleColIndices.map((i) => columnKeys[i]).filter(Boolean)
    : columnKeys;
  const handleCopyRow = (e, row) => {
    e.stopPropagation();
    const line = copyRowKeys.map((k) => row[k] ?? "").join("\t");
    navigator.clipboard?.writeText(line);
  };

  const handleSort = (key) => {
    setSortKey((prev) => (prev === key ? prev : key));
    setSortAsc((prev) => {
      if (sortKey === key) return !prev;
      return true;
    });
  };

  const handleRowClick = (row, rowIndex) => {
    if (rowClickNavigate && typeof rowClickNavigate === "function") {
      rowClickNavigate(row, rowIndex);
      return;
    }
    const id = getRowId ? getRowId(row) : row.id;
    if (id != null && rowClickNavigate === "enquiry_profile") {
      navigate("/enquiry_profile", { state: { enquiry: row } });
    }
  };

  const getCellValue = (row, colIdx, keys = columnKeys, cols = columns) => {
    const key = keys[colIdx];
    if (key && row[key] !== undefined) return row[key];
    const col = cols[colIdx];
    const k = typeof col === "object" && col.key != null ? col.key : col;
    return row[k] ?? row[col] ?? "";
  };

  const handleAction = (e, action, row) => {
    e.stopPropagation();
    if (action.action === "open_enquiry" && action.id != null) {
      navigate("/enquiry_profile", { state: { enquiry: row } });
    } else if (action.action === "edit_enquiry" && action.id != null) {
      navigate("/edit-enquiry", { state: { enquiry: row } });
    } else if (typeof action.onClick === "function") {
      action.onClick(row);
    }
  };

  if (!Array.isArray(columns) || columns.length === 0) {
    return null;
  }

  const hasRowClick = rowClickNavigate || (Array.isArray(actions) && actions.some((a) => a.action === "open_enquiry"));
  const displayColumns = effectiveColumns.length ? effectiveColumns : columns;
  const displayColumnKeys = effectiveColumns.length ? effectiveColumnKeys : columnKeys;

  return (
    <div className="mb-2">
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
        {title && (
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: "text.primary", fontSize: "1rem" }}>
            {title}
          </Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search…"
            value={quickSearch}
            onChange={(e) => {
              setQuickSearch(e.target.value);
              setPage(1);
            }}
            sx={{ width: 160 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Export to Excel">
            <IconButton size="small" onClick={handleExportExcel} aria-label="Export to Excel">
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to CSV">
            <IconButton size="small" onClick={handleExportCsv} aria-label="Export to CSV">
              <TableChartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy table">
            <IconButton size="small" onClick={handleCopyTable} aria-label="Copy table">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            endIcon={<ExpandMoreIcon />}
            onClick={(e) => setShowMoreAnchor(e.currentTarget)}
            sx={{ textTransform: "none", minWidth: "auto" }}
          >
            Show {effectivePageSize === sortedRows.length ? "all" : effectivePageSize}
          </Button>
          <Menu anchorEl={showMoreAnchor} open={!!showMoreAnchor} onClose={() => setShowMoreAnchor(null)}>
            {PAGE_SIZE_OPTIONS.map((n) => (
              <MenuItem
                key={n}
                onClick={() => {
                  setPageSize(n);
                  setPage(1);
                  setShowMoreAnchor(null);
                }}
              >
                {n} rows
              </MenuItem>
            ))}
            <MenuItem
              onClick={() => {
                setPageSize("all");
                setPage(1);
                setShowMoreAnchor(null);
              }}
            >
              All
            </MenuItem>
          </Menu>
          <Tooltip title="Hide columns">
            <IconButton size="small" onClick={(e) => setHideColAnchor(e.currentTarget)} aria-label="Hide columns">
              <ViewColumnIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={hideColAnchor} open={!!hideColAnchor} onClose={() => setHideColAnchor(null)}>
            {columns.map((col, i) => {
              const label = typeof col === "string" ? col : (col.label || col.key || String(col));
              const visible = visibleColIndices == null || visibleColIndices.includes(i);
              return (
                <MenuItem
                  key={i}
                  onClick={() => {
                    setVisibleColIndices((prev) => {
                      if (prev == null) {
                        const next = columns.map((_, j) => j).filter((j) => j !== i);
                        return next.length ? next : null;
                      }
                      if (prev.includes(i)) return prev.length <= 1 ? null : prev.filter((j) => j !== i);
                      return [...prev, i].sort((a, b) => a - b);
                    });
                    setHideColAnchor(null);
                  }}
                >
                  <FormControlLabel
                    control={<Checkbox size="small" checked={visible} />}
                    label={label}
                    sx={{ pointerEvents: "none" }}
                  />
                </MenuItem>
              );
            })}
          </Menu>
        </Box>
      </Box>
      <Paper variant="outlined" className={styles.tableContainer} sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "action.hover" }}>
              {displayColumns.map((col, i) => {
                const key = displayColumnKeys[i];
                const isSorted = sortKey === key;
                return (
                  <TableCell
                    key={i}
                    sx={{ fontWeight: 600, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" }}
                    onClick={() => handleSort(key)}
                  >
                    {typeof col === "string" ? col : col.label || col.key || col}
                    {isSorted && (sortAsc ? " ↑" : " ↓")}
                  </TableCell>
                );
              })}
              <TableCell sx={{ fontWeight: 600, width: 120 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={displayColumns.length + 1} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No rows to display.
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row, idx) => {
                const rowKey = getRowId ? getRowId(row) : row.id ?? idx;
                const rowActions = Array.isArray(row.actions) ? row.actions : actions;
                return (
                  <TableRow
                    key={rowKey}
                    hover
                    onClick={() => handleRowClick(row, start + idx)}
                    className={styles.tableRow}
                    sx={{ cursor: hasRowClick ? "pointer" : "default" }}
                  >
                    {displayColumns.map((col, i) => {
                      const value = getCellValue(row, i, displayColumnKeys, displayColumns);
                      const key = displayColumnKeys[i];
                      const isEmail = key === "email" || (typeof col === "object" && col.key === "email");
                      const isPhone = key === "phone_number" || key === "phone" || (typeof col === "object" && (col.key === "phone_number" || col.key === "phone"));
                      const display = formatCellValue(value);
                      return (
                        <TableCell
                          key={i}
                          sx={{
                            fontSize: "0.875rem",
                            "& .copyIconBtn": { opacity: 0, transition: "opacity 0.15s" },
                            "&:hover .copyIconBtn": { opacity: 1 },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {isEmail && value ? (
                              <a href={`mailto:${value}`} className={styles.emailLink} onClick={(e) => e.stopPropagation()}>
                                {display}
                              </a>
                            ) : isPhone && value ? (
                              <a href={`tel:${value}`} className={styles.phoneLink} onClick={(e) => e.stopPropagation()}>
                                {display}
                              </a>
                            ) : (
                              display
                            )}
                            <IconButton
                              className="copyIconBtn"
                              size="small"
                              sx={{ p: 0.25, flexShrink: 0 }}
                              onClick={(e) => handleCopyCell(e, value)}
                              aria-label="Copy cell"
                            >
                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      );
                    })}
                    <TableCell onClick={(e) => e.stopPropagation()} sx={{ fontSize: "0.875rem" }}>
                      <Tooltip title="Copy row">
                        <IconButton size="small" onClick={(e) => handleCopyRow(e, row)} sx={{ mr: 0.5 }}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {(row.actions || actions).map((action, aIdx) => (
                        <Button
                          key={aIdx}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, textTransform: "none", color: "primary.main", borderColor: "primary.main" }}
                          onClick={(e) => handleAction(e, action, row)}
                        >
                          {action.label || action.action}
                        </Button>
                      ))}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 0.5, mt: 1.5 }}>
        <Tooltip title="Export to Excel">
          <IconButton size="small" onClick={handleExportExcel} aria-label="Export to Excel">
            <FileDownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Export to CSV">
          <IconButton size="small" onClick={handleExportCsv} aria-label="Export to CSV">
            <TableChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy table">
          <IconButton size="small" onClick={handleCopyTable} aria-label="Copy table">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button
          size="small"
          endIcon={<ExpandMoreIcon />}
          onClick={(e) => setShowMoreAnchor(e.currentTarget)}
          sx={{ textTransform: "none", minWidth: "auto" }}
        >
          Show {effectivePageSize === filteredRows.length ? "all" : effectivePageSize}
        </Button>
        <Tooltip title="Hide columns">
          <IconButton size="small" onClick={(e) => setHideColAnchor(e.currentTarget)} aria-label="Hide columns">
            <ViewColumnIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {totalPages > 1 && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2, py: 1.5, flexWrap: "wrap", gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {start + 1}–{Math.min(start + effectivePageSize, filteredRows.length)} of {filteredRows.length}
          </Typography>
          <Pagination
            size="small"
            count={totalPages}
            page={safePage}
            onChange={(_, p) => setPage(p)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
      {Array.isArray(suggestions) && suggestions.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Suggested follow-ups
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {suggestions.map((text, i) => (
              <Button
                key={i}
                size="small"
                variant="outlined"
                sx={{ textTransform: "none" }}
                onClick={() => onSuggestionClick?.(text)}
              >
                {text}
              </Button>
            ))}
          </Box>
        </Box>
      )}
    </div>
  );
}

export default AIResponseTable;
